/**
 * Option-step SEMANTICS — the one module that owns how every consumer
 * interprets a flow's option steps (deepening #1).
 *
 * These conventions used to be re-implemented inline at each call site (the
 * "Other…" dual-check in 4-5 files; the `maxSelections === 0` cap rule in the
 * admin editor vs the public configurator). Dropping a fallback or flipping a
 * comparison in any one site silently broke a box — classic shallow-knowledge
 * scatter. Centralising them here gives one deep module that public AND admin
 * both import, so the rule is defined (and tested) exactly once.
 *
 * Parameters are MINIMAL structural shapes, not the full `Option`/`Step`
 * types, so both the schema types (admin) and the public app's adapted types
 * satisfy them. The interface declares exactly what each rule needs — nothing
 * more.
 */

/** The fields needed to decide whether an option is a free-text "Other…". */
export interface OtherOptionLike {
  readonly id: string;
  readonly isOther?: boolean;
}

/** The field needed to read a step's selection cap. */
export interface SelectionCapLike {
  readonly maxSelections?: number;
}

/**
 * True iff an option is the free-text "Other…" choice.
 *
 * Two ways an option qualifies, checked together so legacy + new flows agree:
 *   - `isOther === true` — the first-class flag authored in the admin (N2b+);
 *   - its id contains "other" (case-insensitive) — the legacy magic-id
 *     convention used by bundled Spring/Office before the flag existed
 *     (`other`, `other-merch`).
 *
 * Every consumer (configurator render, selection state, checkout render,
 * submission builder) MUST decide this identically — dropping the legacy
 * fallback in any one site silently breaks the bundled boxes. That is exactly
 * why this lives in one place.
 */
export function isOtherOption(
  option: OtherOptionLike | null | undefined,
): boolean {
  if (!option) return false;
  return option.isOther === true || option.id.toLowerCase().includes('other');
}

/**
 * The ONE selection-cap convention for an option step:
 *
 *   `0` OR absent → UNLIMITED (`Infinity`)
 *   `1`           → single-select (radio)
 *   `N` (N ≥ 2)   → hard cap of N
 *
 * Why absent = unlimited (not single): a bundled/legacy doc that omits the
 * field is treated as unlimited, never silently capped to one — the safe
 * direction for a customer-facing order. Admin-authored steps always carry a
 * `maxSelections` (the schema makes it required).
 */
export function getMaxSelections(step: SelectionCapLike): number {
  const max = step.maxSelections;
  if (max === undefined || max === 0) return Infinity;
  return max;
}

/** True when the step allows unlimited selections (`maxSelections` 0/absent). */
export function isUnlimitedSelection(step: SelectionCapLike): boolean {
  return getMaxSelections(step) === Infinity;
}

/** True when the step allows exactly one selection (`maxSelections === 1`). */
export function isSingleSelect(step: SelectionCapLike): boolean {
  return getMaxSelections(step) === 1;
}

/** True when the step lets the customer pick more than one option. */
export function isMultiSelect(step: SelectionCapLike): boolean {
  return getMaxSelections(step) > 1;
}
